import React, { useEffect, useRef } from 'react';
import AppEditStepModal from '../content/editModals/editStep';
import { Dialog, FluentProvider, webLightTheme } from '@fluentui/react-components';
import AppEditActivityGroupModal from '../content/editModals/editActivityGroup';
import AppDeleteModal from '../content/editModals/delete';
import AppCreateStepModal from '../content/editModals/newStep';
import AppCreateActivityGroupModal from '../content/editModals/newActivityGroup';
import { copyStyles } from '../utils/copyStyles';
import AppCreateActivityModal from '../content/editModals/newActivity';
import AppEditActivityModal from '../content/editModals/editActivity';
import ReactDOM from 'react-dom';

const dialogs = {
  editStep: AppEditStepModal,
  editActivityGroup: AppEditActivityGroupModal,
  editActivity: AppEditActivityModal,
  delete: AppDeleteModal,
  createStep: AppCreateStepModal,
  createActivityGroup: AppCreateActivityGroupModal,
  createActivity: AppCreateActivityModal,
};
export type DialogTypes = keyof typeof dialogs;
type DialogPayloadTypes = {
  [P in DialogTypes]: Omit<Parameters<(typeof dialogs)[P]>[0], 'dialogOptions'>;
};

export type DialogContextState<K extends DialogTypes = DialogTypes> = {
  [P in DialogTypes]: { type: P; data: DialogPayloadTypes[P] };
}[K];

const context = React.createContext<{
  openDialog: (dialog: DialogContextState) => void;
  forceCloseDialog: () => void;
}>({ openDialog: () => {}, forceCloseDialog: () => {} });

export function useDialogActions() {
  return React.useContext(context);
}

function createDialog(state: DialogContextState, onCloseDialog: () => void, useSeparateWindow: boolean) {
  const { type, data } = state;
  const componentF = dialogs[type];

  // Somehow typescript does not infer that type and data are coupled, hence we work our way out with any...:(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return React.createElement(componentF as any, {
    ...data,
    dialogOptions: { onCloseDialog: onCloseDialog, inSeparateWindow: useSeparateWindow },
  });
}

export default function AppDialogContextProvider(props: React.PropsWithChildren): JSX.Element {
  const [state, setState] = React.useState<DialogContextState<DialogTypes> | 'none'>('none');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [useSeparateWindow, setUseSeparateWindow] = React.useState<boolean>(true);
  const dialogWindow = useRef<Window | null>(null);
  const dialogWindowContainer = useRef<HTMLDivElement>(document.createElement('div'));

  function openDialog(dialog: DialogContextState) {
    setState(() => dialog);
  }
  function closeDialog() {
    setState(() => 'none');
  }
  const forceCloseDialog = closeDialog;

  let dialogComponent: JSX.Element = <></>;
  if (state === 'none') {
    dialogComponent = <></>;
  } else {
    dialogComponent = createDialog(state, closeDialog, useSeparateWindow);
  }
  const requireSeparateWindow = state !== 'none' && useSeparateWindow;
  const separateWindowIsOpen = !!(dialogWindow.current && !dialogWindow.current.closed);

  useEffect(() => {
    // only run when there is no pop up open yet,
    // Open on state change so their is an actual user interaction that will allow the opening of a new window
    if (state === 'none' || !useSeparateWindow) {
      return;
    }
    if (!dialogWindow.current) {
      dialogWindow.current = window.open('', '', 'width=auto,height=auto');
    }
    if (!dialogWindow.current) {
      return;
    }
    if (dialogWindow.current.closed) {
      dialogWindow.current.open('', '', 'width=auto,height=auto');
    }
    dialogWindow.current.document.body.appendChild(dialogWindowContainer.current);
    dialogWindow.current.addEventListener('beforeunload', closeDialog);
    copyStyles(document, dialogWindow.current.document);

    window.addEventListener('unload', () => {
      if (dialogWindow.current) {
        dialogWindow.current.close();
        dialogWindow.current = null;
      }
    });
  }, [separateWindowIsOpen || requireSeparateWindow, useSeparateWindow, dialogWindow.current]);
  useEffect(() => {
    if (separateWindowIsOpen && dialogWindow.current && state !== 'none') {
      dialogWindow.current.focus();
    }
  }, [state, useSeparateWindow]);

  const newWindowChildren = (
    <FluentProvider theme={webLightTheme}>
      {state !== 'none' ? dialogComponent : <span>No dialog open</span>}
    </FluentProvider>
  );
  const portal = ReactDOM.createPortal(newWindowChildren, dialogWindowContainer.current);

  return (
    <context.Provider value={{ openDialog, forceCloseDialog }}>
      {!useSeparateWindow && <Dialog open={state !== 'none'}>{dialogComponent}</Dialog>}
      {useSeparateWindow && portal}
      {props.children}
    </context.Provider>
  );
}
